/**
 * Job Briefing resolver — derives the few facts a contractor actually needs
 * to decide whether to take a job, and to show up prepared:
 *
 *   • Arrival window (from scheduledStartTime / scheduledInstallDate)
 *   • Estimated days (from JobSpec phases × structured complexity facts)
 *   • Suggested crew size (binary facts, not "complexity" judgement)
 *   • Risk flags  (binary facts only — never PM opinion)
 *   • Scope photos (JobSpec scopePhotos > job.photos[] from consultation)
 *
 * Hard rules (per memory):
 *   - Never expose contractValue, costs, or revenue.
 *   - Never ask reps/PMs to judge complexity. Inputs are structured/binary.
 *   - Pure function. No mutations. No side effects.
 *   - Defaults are conservative (over-staff, longer window) — never under.
 */

import type { Job } from "@/data/types";
import type { JobSpec } from "@/data/jobSpec";

export interface JobBriefing {
  arrival: {
    /** Human label, e.g. "Tue · 8:00–9:00 AM" or "Tue · arrival window TBD". */
    label: string;
    date?: string;
    windowStart?: string;
    windowEnd?: string;
  };
  estimatedDays: number;
  suggestedCrewSize: number;
  riskFlags: BriefingFlag[];
  /** Up to 6 photo URLs. Empty array if none on file. */
  scopePhotos: string[];
  /** Plain-language scope sentence, safe to show contractor. */
  scopeSummary: string;
}

export interface BriefingFlag {
  key:
    | "plumbing_relocation"
    | "drain_relocation"
    | "layout_change"
    | "second_floor"
    | "tile_demo"
    | "custom_glass"
    | "permit_required"
    | "unknown_wall_condition";
  label: string;
}

const FLAG_LABEL: Record<BriefingFlag["key"], string> = {
  plumbing_relocation: "Plumbing relocation",
  drain_relocation: "Drain relocation",
  layout_change: "Layout change",
  second_floor: "Second-floor / access constraints",
  tile_demo: "Tile demo required",
  custom_glass: "Custom glass",
  permit_required: "Permit required",
  unknown_wall_condition: "Unknown wall condition",
};

/* ── Internal: extract structured facts ───────────────────────────────── */

function deriveFlags(job: Job): BriefingFlag[] {
  const cfg = job.projectConfig;
  const flags: BriefingFlag["key"][] = [];

  if (cfg?.plumbingRelocation || cfg?.structuralChanges?.movingPlumbing) {
    flags.push("plumbing_relocation");
  }
  if (cfg?.structuralChanges?.movingDrain) flags.push("drain_relocation");
  if (cfg?.structuralChanges?.subfloorMod) flags.push("layout_change");
  if (cfg?.structuralChanges?.windowMod === "Yes") {
    flags.push("unknown_wall_condition");
  }
  if (cfg?.customModifications) flags.push("custom_glass");
  if (job.permitRequired) flags.push("permit_required");

  return flags.map((key) => ({ key, label: FLAG_LABEL[key] }));
}

/* ── Estimated days: phases + risk uplift ─────────────────────────────── */

function deriveEstimatedDays(
  job: Job,
  spec: JobSpec | undefined,
  flagCount: number,
): number {
  // Honour explicit scheduled span first.
  if (job.scheduledShifts && job.scheduledShifts > 0) {
    return job.scheduledShifts;
  }
  const phases = spec?.phases?.length ?? 0;
  // Rough rule: every ~3 phases ≈ 1 install day, min 1.
  let days = Math.max(1, Math.ceil(phases / 3));
  // Risk uplift — each structural flag adds ~½ day, capped.
  if (flagCount >= 2) days += 1;
  if (flagCount >= 4) days += 1;
  return Math.min(days, 5);
}

/* ── Suggested crew size: binary structural facts ─────────────────────── */

function deriveCrewSize(flags: BriefingFlag[], days: number): number {
  // Default crew of 2 for any standard install.
  let crew = 2;
  const keys = new Set(flags.map((f) => f.key));
  if (keys.has("plumbing_relocation") || keys.has("drain_relocation")) crew = 3;
  if (keys.has("layout_change") && keys.has("tile_demo")) crew = 3;
  if (days >= 3) crew = Math.max(crew, 3);
  return crew;
}

/* ── Arrival window ───────────────────────────────────────────────────── */

function fmtTime(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function deriveArrival(job: Job): JobBriefing["arrival"] {
  const date = job.scheduledInstallDate;
  const start = job.scheduledStartTime;
  const end = job.scheduledEndTime;
  const dayLabel = date
    ? new Date(date + "T12:00:00").toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : "Date TBD";

  if (start && end) {
    const a = fmtTime(start);
    const b = fmtTime(end);
    return {
      label: `${dayLabel} · ${a}–${b}`,
      date,
      windowStart: start,
      windowEnd: end,
    };
  }
  if (job.timeSlot) {
    return { label: `${dayLabel} · ${job.timeSlot}`, date };
  }
  return { label: `${dayLabel} · arrival window TBD`, date };
}

/* ── Scope photos: JobSpec hint > attachment URLs > job.photos ─────── */

function deriveScopePhotos(job: Job, attachmentUrls?: string[]): string[] {
  // Priority: curated scopePhotos (PM-set) → real attachment URLs → legacy job.photos
  const curated = (job as unknown as { scopePhotos?: string[] }).scopePhotos;
  const list =
    curated && curated.length > 0 ? curated :
    attachmentUrls && attachmentUrls.length > 0 ? attachmentUrls :
    job.photos ?? [];
  return list.filter(Boolean).slice(0, 6);
}

/* ── Public API ────────────────────────────────────────────────────────── */

export function getJobBriefing(job: Job, spec?: JobSpec, attachmentUrls?: string[]): JobBriefing {
  const flags = deriveFlags(job);
  const estimatedDays = deriveEstimatedDays(job, spec, flags.length);
  const suggestedCrewSize = deriveCrewSize(flags, estimatedDays);
  const arrival = deriveArrival(job);
  const scopePhotos = deriveScopePhotos(job, attachmentUrls);
  const scopeSummary =
    job.scopeSummary?.trim() ||
    spec?.label ||
    job.projectType ||
    "Scope details to follow.";
  return {
    arrival,
    estimatedDays,
    suggestedCrewSize,
    riskFlags: flags,
    scopePhotos,
    scopeSummary,
  };
}
